import axios from 'axios'
import config from './config';
import authService, { SESSION_EXPIRED_EVENT } from './auth-service';
import { postApi } from './apiBase';
import { refreshAccessToken } from './azure/acquireToken';
import Swal from 'sweetalert2';
import i18next from 'i18next';
// The token refresh in flight. Requests that 401 together wait on this one call instead of
// each starting their own, and it is cleared once settled so a later expiry can refresh again.
let refreshPromise = null;

// Shared Swal config — avoids recreating identical objects on every error
const SWAL_BASE = {
    confirmButtonColor: '#6AC5DA',
    allowOutsideClick: false,
    showClass: { backdrop: 'swal2-noanimation', popup: '', icon: '' },
    hideClass: { popup: '' },
};

// Dismiss any existing modal before showing a new one (prevents DOM accumulation).
// Must return the Swal promise so callers can run follow-up work (e.g. redirect) on OK.
const showErrorModal = (options) => {
    Swal.close();
    return Swal.fire({ ...SWAL_BASE, ...options });
};

// Signal to the caller only when this interceptor actually displayed a modal.
// A suppressed popup or an unhandled response must still be reported by the caller.
const showErrorModalForRequest = (error, options) => {
    const result = showErrorModal(options);
    error._globalErrorModalShown = true;
    return result;
};

// Hard-navigate to the signout screen. The app's BrowserRouter uses basename={'#'},
// so every route lives entirely in the URL fragment. Navigating between two such
// URLs is a same-document "fragment navigation" that browsers never reload for and
// that never fires popstate — so a plain location.replace()/href assignment updates
// the address bar but leaves the on-screen route exactly where it was. A full reload
// forces the app to reboot and read the (already-updated) hash on startup instead.
const redirectToSignout = () => {
    window.location.hash = '/signout';
    window.location.reload(true);
};

const logoutAndRedirectToSignout = () => {
    authService.logout(redirectToSignout);
};

// Used after Unauthorized (403): keep the user on-page if still logged in;
// only sign out when the session is actually gone/expired.
const signoutIfSessionExpired = () => {
    if (!authService.isSessionActive()) {
        logoutAndRedirectToSignout();
    }
};

const showLoginRequiredModal = (error) => {
    // Tell idle handler not to offer "extend" over a dead auth session.
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    return showErrorModalForRequest(error, {
        text: i18next.t('popupErrorMessages.401Text'),
        confirmButtonText: i18next.t('buttonLabel.ok'),
        // willClose covers OK and any programmatic dismiss so redirect cannot be skipped.
        willClose: logoutAndRedirectToSignout,
    });
};

const executeInterceptors = () => {
    //Loader Globally
    // Reuse existing loader element or create one:
    let element = document.getElementById('api-interceptor-loader');
    if (!element) {
        element = document.createElement("div");
        element.id = 'api-interceptor-loader';
        document.body.appendChild(element);
    }


    // set base url
    const { base_api_url } = config.common;
    //set URL to request the access token
    axios.defaults.baseURL = base_api_url;

    // Read auth state directly from localStorage on every request.
    // A previous closure-scoped cache held the OIDC id_token from the IDS
    // token exchange and never refreshed when setAccessAndRefreshToken stored
    // the Elements JWT, producing a 403 on the first call after SSO login.
    const getAccessToken = () => `Bearer ${localStorage.getItem('access_token')}`;
    const getRefreshToken = () => localStorage.getItem('refresh_token');
    const getAuthMode = () => localStorage.getItem('authMode');

    // Exchanges the refresh token for new tokens, sharing one call between concurrent 401s.
    // RefreshToken returns a ServiceResponse, so the tokens are under response.data.data.
    const refreshTokens = (base_api_url) => {
        if (!refreshPromise) {
            refreshPromise = postApi(`${base_api_url}/api/Login/RefreshToken`, { 'refreshToken': getRefreshToken() })
                .then(({ status: statusCode, data }) => {
                    const tokens = data && data.data;
                    if ((statusCode === 200 || statusCode === 201) && tokens && tokens.token) {
                        authService.setAccessAndRefreshToken(tokens);
                        axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.token}`;
                        return tokens.token;
                    }
                    throw new Error('Token refresh failed');
                })
                .finally(() => {
                    refreshPromise = null;
                });
        }
        return refreshPromise;
    };

    //Set Request
    axios.interceptors.request.use(async (cfg) => {
        // refreshAccessToken method is the one which makes acquireTokenSilent call .
        // cfg.headers['TenantId'] = 1;
        if (getAuthMode() === 'azureClient') {
            const token = await refreshAccessToken();
            if (token) {
                cfg.headers['Authorization'] = `Bearer ${token}`;
            }
        } else if (localStorage.getItem('expirePassword')) {
            cfg.headers.Authorization = ''
        } else {
            cfg.headers.Authorization = getAccessToken();
        }

        if (cfg.url === `${cfg.baseURL}/api/Login/Token` && cfg.method == "get") {
            cfg.headers['Module'] = 'Common'
        } else if (cfg.url?.includes('/api/tools/search/quick')) {
            cfg.headers['Module'] = 'Common';
        } else if (cfg.url?.includes('/api/tools/deeplink/')) {
            cfg.headers['Module'] = 'Common';
        } else if (cfg.url?.includes('/api/tools/assistant')) {
            // Aristotle assistant session mint — callable from any page, so it
            // must resolve before the window.location.href module branches below.
            cfg.headers['Module'] = 'Common';
        } else if (cfg.url.includes(`${cfg.baseURL}/api/file`)) {
            cfg.headers['Module'] = 'Common';
        } else if (cfg.url.includes(`${cfg.baseURL}/api/manager/configuration/role-administration/clear-user-semaphores`)) {
            cfg.headers['Module'] = 'Common';
        } else if (cfg.url === `${cfg.baseURL}/api/manager/configuration/settings` && cfg.method == "post") {
            cfg.headers['Module'] = 'CAMS Manager'
        }
        else if (cfg.url === `${cfg.baseURL}/api/student/lookup` && cfg.method == "post") {
            cfg.headers['Module'] = 'Common'
        }
        else if (cfg.url.includes('api/manager/backgroundtask') && (cfg.method == "get" || cfg.url.includes('/notifications/'))) {
            // The notification bell's endpoints are Common-module. The list is a GET, but mark-read is
            // a PUT (/notifications/{id}/read) — without this it would fall through to the page-based
            // branches and be authorized as the current page's module, so the gateway 403s it.
            cfg.headers['Module'] = 'Common'
        }
        else if (cfg.url === `${cfg.baseURL}/api/manager/configuration/user-administration/sidebar-menu` && cfg.method == "get") {

            cfg.headers['Module'] = 'Common'

        } else if (cfg.url === `${cfg.baseURL}/api/manager/configuration/user-administration/report-sidebar-menu` && cfg.method == "get") {

            cfg.headers['Module'] = 'Common'

        } else if (cfg.url === `${cfg.baseURL}/api/manager/configuration` && cfg.method == "get") {

            cfg.headers['Module'] = 'Common'

        } else if ((cfg.url === `${cfg.baseURL}/api/manager/configuration/settings`) || (cfg.url === `${cfg.baseURL}/api/manager/lookup-table-options/risk-indicator-setup/rnd-task`)) {

            cfg.headers['Module'] = 'Common'

        } else if ((cfg.url.indexOf('/api/manager/configuration/user-administration/user-role-order') > -1) || (cfg.url.indexOf('health-check') > -1)) {

            cfg.headers['Module'] = 'Common'

        } else if ((cfg.url.indexOf('/api/tools/data-query/select-items') > -1) || (cfg.url.indexOf('/api/tools/data-query/list') > -1) || (cfg.url.indexOf('/api/student/search/get-select-items-list') > -1)) {

            cfg.headers['Module'] = 'Common'

        } else if (cfg.url.indexOf('/api/manager/import-export/transformation-template') > -1) {

            cfg.headers['Module'] = 'Common'

        }else if (cfg.url.indexOf('/api/financial-aid/student-status/extra-curricular-Deleted') > -1) {

            cfg.headers['Module'] = 'Common'

        } else if ((cfg.url.indexOf('/api/manager/report-configuration') > -1) || (cfg.url.indexOf('/api/manager/configuration/user-administration/change-password') > -1)) {

            cfg.headers['Module'] = 'Common'

        } else if (cfg.url.indexOf('/api/manager/lookup-table-options/employer') > -1) {

            cfg.headers['Module'] = 'CAMS Manager'

        } else if (window.location.href.indexOf('/#/admissions/') > -1) {

            cfg.headers['Module'] = 'Admissions'

        } else if (window.location.href.indexOf('/#/registration/') > -1) {

            cfg.headers['Module'] = 'Registration'

        } else if (window.location.href.indexOf('/#/billing/') > -1) {

            cfg.headers['Module'] = 'Billing'

        } else if (window.location.href.indexOf('/#/financial-aid/') > -1) {

            cfg.headers['Module'] = 'Financial Aid'

        } else if (window.location.href.indexOf('/#/system-manager/') > -1) {

            cfg.headers['Module'] = 'CAMS Manager'

        } else if (window.location.href.indexOf('/#/tools/') > -1) {

            cfg.headers['Module'] = 'Tools'

        } else if (cfg.url.indexOf('/api/manager/') > -1) {

            cfg.headers['Module'] = 'CAMS Manager'

        } else if (cfg.url.indexOf('/api/tools/') > -1) {

            cfg.headers['Module'] = 'Tools'

        } else if ((window.location.href.indexOf('/admissions/') > -1) || (window.location.href.indexOf('/admission/') > -1)) {

            cfg.headers['Module'] = 'Admissions'

        }
        else if (window.location.href.indexOf('/registration/') > -1) {

            cfg.headers['Module'] = 'Registration'

        } else if (window.location.href.indexOf('/billing/') > -1) {

            cfg.headers['Module'] = 'Billing'

        } else if (window.location.href.indexOf('/financial-aid/') > -1) {

            cfg.headers['Module'] = 'Financial Aid'

        } else if (window.location.href.indexOf('/system-manager/') > -1) {

            cfg.headers['Module'] = 'CAMS Manager'

        } else if (window.location.href.indexOf('/tools/') > -1) {

            cfg.headers['Module'] = 'Tools'

        }
        else if (cfg.url.indexOf('/api/amenities/') > -1) {
            cfg.headers['Module'] = 'Student Life'
        }
        else if (window.location.href.indexOf('/student-life/') > -1) {
            cfg.headers['Module'] = 'Student Life'
        }

        if(window.location.href.indexOf('/faculty/add-faculty') > -1 && cfg.url.indexOf('/api/contact/search') > -1){
            cfg.headers['Module'] = 'Admissions'
        }

        if (cfg.url.indexOf('/api/manager/engagement/engagement-url') > -1) {
            cfg.headers['Module'] = 'Common'
        }

        if (cfg.url.indexOf('/api/manager/featureflags') > -1) {
            cfg.headers['Module'] = 'CAMS Manager'
        }

        // Data Dictionary is a Tools feature, but its panel/icon is reachable from any page.
        // Override the page-location-derived module so the gateway permission check matches
        // the data dictionary Ele_Authorization_Lookup rows, whose Module is 'Tools'. The
        // gateway compares this header to that column verbatim, so the two must move together:
        // when the feature moved out of System Manager, this stopped matching and every
        // endpoint answered 403.
        // The per-page contextual panel (/by-route) stays available to ALL authenticated
        // users via the 'Common' module; the admin endpoints are gated to Tools.
        if (cfg.url.indexOf('/api/manager/data-dictionary/by-route') > -1) {
            cfg.headers['Module'] = 'Common'
        } else if (cfg.url.indexOf('/api/manager/data-dictionary') > -1) {
            cfg.headers['Module'] = 'Tools'
        }

        // A request that outlives its screen cannot be identified by the current route.
        if (cfg._module) {
            cfg.headers['Module'] = cfg._module;
        }

        return cfg;
    });

    axios.interceptors.response.use(
        (cfg) => {
            return cfg;
        },
        (error) => {
            //Loader Globally
            document.getElementById("root").style.display = "block";
            document.getElementsByTagName('body')[0].classList.remove('loader-external-api');
            element.classList.remove('api-request-loader');
            const status = error.response && error.response.status;
            const url = error.config && error.config.url;
            const user = getAuthMode();
            const originalRequest = error.config;
            const { base_api_url } = config.common;
            if (!(status || url)) {
                return Promise.reject(error);
            }
            // Callers that render their own error UI (e.g. gate page, sidebar autocomplete)
            // can opt out of the global SweetAlert2 popup by passing _suppressErrorPopup in the axios config.
            // Auth/session handling (401 refresh + 403 dead-session signout) still runs so forced logout keeps working.
            const suppressPopup = Boolean(error.config && error.config._suppressErrorPopup);
            /// Access denial status code
            if ((status === 424 || status === 423) && !suppressPopup) {
                // Admissions ServiceResponse places validation messages in errorCode;
                // Manager/Login Response<string> instead uses data.
                const errorMsg = error?.response?.data?.data || error?.response?.data?.errorCode || i18next.t('popupErrorMessages.409Text')
                showErrorModalForRequest(error, { text: errorMsg });
            }
            if (status === 409 && !suppressPopup) {
                const errorMsg = (error?.response?.data?.data) ? error?.response?.data?.data : i18next.t('popupErrorMessages.409Text')
                showErrorModalForRequest(error, { html: errorMsg });
            }
            if ((status === 400 || status === 404) && !suppressPopup) {
                let errorMsg = i18next.t('popupErrorMessages.404Text');
                if (error?.response?.data?.data && error?.response?.data?.data.length > 0) {
                    errorMsg = error.response.data.data;
                } else if (typeof error?.response?.data === 'string' && error.response.data.length > 0) {
                    errorMsg = error.response.data;
                } else if (error?.response?.data?.errorCode) {
                    errorMsg = error.response.data.errorCode;
                } else if (error?.response?.data?.errors) {
                    const validationErrors = Object.values(error.response.data.errors).flat();
                    if (validationErrors.length > 0) {
                        errorMsg = validationErrors.join('\n');
                    }
                }
                showErrorModalForRequest(error, { text: errorMsg });
            }
            if ((status === 500 || status === 503) && !suppressPopup) {
                showErrorModalForRequest(error, { text: i18next.t('popupErrorMessages.500Text') });
            }
            if (status !== 401 && url === `${base_api_url}/api/Login/RefreshToken`) {
                window.location.replace('/logout');
                return Promise.reject(error);
            }
            if (status === 401) {
                if (getAccessToken() !== null && user === 'cams') {
                    /// need to check: handle at the login time if user unauthorized
                    // _retry lets each request retry once, and the refresh call itself is never retried.
                    if (!originalRequest._retry && url !== `${base_api_url}/api/Login/RefreshToken`) {
                        originalRequest._retry = true;
                        return refreshTokens(base_api_url)
                            // The request interceptor attaches the new access token from localStorage.
                            .then(() => axios(originalRequest), () => {
                                showLoginRequiredModal(error);
                                return Promise.reject(error);
                            });
                    }
                }
            }
            if (status === 403) {
                if (!suppressPopup) {
                    // Wait for OK so a true permission 403 does not bounce the user away.
                    showErrorModalForRequest(error, {
                        text: i18next.t('popupErrorMessages.403Text'),
                        confirmButtonText: i18next.t('buttonLabel.ok')
                    }).then(signoutIfSessionExpired);
                } else {
                    // Background/suppressed callers skip the alert but must still leave a dead session.
                    signoutIfSessionExpired();
                }
            }

            //Loader Globally
            if (document.getElementsByClassName("api-request-loader").length > 0) {
                document.getElementsByClassName("api-request-loader")[0].style.display = "none";
            }

            return Promise.reject(error);
        });
};

export { executeInterceptors };
